import { ReferencedEntry } from 'contentful';
import React from 'react';

import { HasExternalSources, HasImage, HasMedia, HasMobileImage, HasVideo, MediaSourceType } from 'src/api/contentful/contentTypes.cloverdotcom';
import { useResponsive } from 'src/components/Responsive/Responsive';
import { ContentfulImage } from '../Contentful/ContentfulImage';
import { Video } from '../Contentful/Video';
import { LazyLoader } from '../LazyLoader/LazyLoader';
import { BasicMedia, BasicMediaProps } from './BasicMedia';
import { ExternalMedia } from './ExternalMedia';
import { IframeVideo } from './IframeVideo';

type MediaProps = Pick<BasicMediaProps, Exclude<keyof BasicMediaProps, 'asset' | 'video'>> & {
  entry?: ReferencedEntry<HasVideo & HasImage & HasMobileImage & HasExternalSources & HasMedia>;
  canRenderExternal?: boolean;
};

const Media = React.forwardRef<Video | ContentfulImage, MediaProps>(({ entry, canRenderExternal, ...otherProps }, ref) => {
  if (!entry || !entry.fields) return null;

  const { image: defaultImage, mobileImage, media, video, youtubeId } = entry.fields;
  const { sourceType = '' } = media && media.fields || {};
  const isVimeo = sourceType === MediaSourceType.VIMEO;

  const { browser } = useResponsive();

  const image = (browser.is.mobile && mobileImage) || defaultImage;

  if (canRenderExternal && youtubeId) {
    return <ExternalMedia youtubeId={youtubeId} {...otherProps} />;
  }

  if (canRenderExternal && isVimeo) {
    return <IframeVideo mediaFields={media && media.fields} {...otherProps} />;
  }

  return (
    <LazyLoader>
      <BasicMedia
        ref={ref as React.Ref<Video | ContentfulImage>}
        asset={image}
        video={video}
        {...otherProps}
      />
    </LazyLoader>
  );
});

type MediaType = typeof Media;

export {
  MediaProps,
  Media,
  MediaType,
};
